import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import ReadAloudButton from "@/components/ReadAloudButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LADDER_RUNGS = [
  { name: "Exposure", hint: "The hidden wound comes into the light" },
  { name: "Truth", hint: "Truth replaces the lie" },
  { name: "Severance", hint: "Cutting off false refuges; forgiving the old" },
  { name: "Surrender", hint: "Letting go of all remaining trauma and lies" },
  { name: "Alignment", hint: "Learning to walk fully in the Spirit of love" },
  { name: "Union", hint: "The apostolic place — becoming love itself" },
  { name: "Glory", display: "Glory (Perpetua's Rung)", hint: "Multiplied fruit — a light on a hill" },
];

function LadderDiagram({ activeRung }) {
  return (
    <aside className="my-10 border border-[#FFE7A0]/30 bg-[#FFE7A0]/[0.04] px-6 sm:px-10 py-8" data-testid="progress-ladder-diagram">
      <div className="font-cinzel text-[0.65rem] sm:text-xs tracking-[0.28em] uppercase text-[#FFE7A0] mb-5 text-center">
        Jacob&rsquo;s Ladder — where you stand now
      </div>
      <ol className="relative pl-10 sm:pl-14">
        <span
          aria-hidden="true"
          className="absolute left-4 sm:left-6 top-2 bottom-2 w-px bg-gradient-to-b from-[#FFE7A0]/60 via-[#FFE7A0]/25 to-[#FFE7A0]/60"
        />
        {LADDER_RUNGS.slice().reverse().map((rung, revIdx) => {
          const idx = LADDER_RUNGS.length - 1 - revIdx;
          const isActive = activeRung && activeRung.toLowerCase() === rung.name.toLowerCase();
          return (
            <li key={rung.name} className="relative mb-4 last:mb-0" data-testid={`progress-ladder-rung-${rung.name.toLowerCase()}`}>
              <span
                aria-hidden="true"
                className={`absolute -left-7 sm:-left-9 top-[0.4rem] h-3 w-3 rounded-full border ${
                  isActive
                    ? "bg-[#FFE7A0] border-[#FFE7A0] shadow-[0_0_12px_rgba(255, 231, 160,0.65)]"
                    : "bg-[#0a0f24] border-[#FFE7A0]/60"
                }`}
              />
              <div className={isActive ? "text-[#FFE7A0]" : "text-[#F0E6D2]/85"}>
                <div className="font-serif-display text-base sm:text-lg leading-tight">
                  {idx + 1}. {rung.display || rung.name}
                  {isActive && (
                    <span className="ml-3 font-cinzel text-[0.55rem] tracking-[0.25em] uppercase text-[#FFE7A0]/85">
                      where you are right now
                    </span>
                  )}
                </div>
                <div className="block text-xs sm:text-sm text-[#F0E6D2]/55 italic font-body font-light mt-0.5">
                  {rung.hint}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      {activeRung && (
        <p className="mt-6 text-center text-xs sm:text-sm text-[#F0E6D2]/60 font-body font-light italic">
          The gold rung marks where the Matrix sees you standing today.
        </p>
      )}
    </aside>
  );
}

export default function ProgressPacket() {
  const { id } = useParams();
  const [packet, setPacket] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      // Retry-with-backoff for cross-worker eventual consistency —
      // see /app/frontend/src/pages/Packet.jsx for the same pattern.
      const delays = [0, 800, 1600, 2500, 4000];
      let lastErrDetail = "";
      let lastErrStatus = 0;
      for (let i = 0; i < delays.length; i += 1) {
        if (!alive) return;
        if (delays[i] > 0) await new Promise((r) => setTimeout(r, delays[i]));
        try {
          const res = await axios.get(`${API}/progress/${id}`);
          if (alive) setPacket(res.data);
          return;
        } catch (e) {
          lastErrStatus = e?.response?.status || 0;
          lastErrDetail = e?.response?.data?.detail || "Could not load progress packet.";
          if (lastErrStatus && lastErrStatus !== 404) {
            if (alive) setError(lastErrDetail);
            return;
          }
        }
      }
      if (alive) setError(lastErrDetail || "Could not load progress packet.");
    })();
    return () => { alive = false; };
  }, [id]);

  if (error) {
    return (
      <main className="min-h-screen rm-cinematic text-[#F0E6D2] px-6 py-16 flex items-center justify-center">
        <div className="text-center" data-testid="progress-packet-error">
          <p className="text-red-300 font-body mb-6">{error}</p>
          <Link to="/progress" className="rm-btn-outline">Try again</Link>
        </div>
      </main>
    );
  }

  if (!packet) {
    return (
      <main className="min-h-screen rm-cinematic text-[#F0E6D2] px-6 py-16 flex items-center justify-center">
        <div className="font-cinzel text-xs sm:text-sm tracking-[0.28em] uppercase text-[#FFE7A0] animate-pulse" data-testid="progress-packet-loading">
          Loading…
        </div>
      </main>
    );
  }

  const pdfUrl = `${API}/progress/${id}/pdf`;

  return (
    <main className="min-h-screen rm-cinematic text-[#F0E6D2] px-6 sm:px-12 md:px-20 py-12" data-testid="progress-packet-page">
      <header className="mb-12 max-w-3xl mx-auto text-center">
        <Link to="/" className="overline text-[#FFE7A0]/70 hover:text-[#FFE7A0]">
          ← The Restoration Matrix
        </Link>
        <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl text-[#FFE7A0] leading-tight" data-testid="progress-packet-title">
          Progress Report — {packet.name}
        </h1>
        {packet.new_rung && (
          <p className="mt-5 font-cinzel text-xs sm:text-sm tracking-[0.28em] uppercase text-[#FFE7A0]/90" data-testid="progress-packet-new-rung">
            You are now standing on · {packet.new_rung}
          </p>
        )}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={pdfUrl}
            className="rm-btn-solid"
            data-testid="progress-packet-download-btn"
          >
            Download the PDF
          </a>
          <ReadAloudButton
            contentSelector='[data-testid="progress-packet-sections"]'
            testId="progress-packet-read-aloud-btn"
          />
        </div>
        <p className="mt-3 text-xs text-[#F0E6D2]/45 italic max-w-md mx-auto">
          This page is ephemeral — save the PDF if you want to keep it.
        </p>
      </header>

      <section className="max-w-3xl mx-auto space-y-12" data-testid="progress-packet-sections">
        {packet.sections.map((sec, i) => {
          const isNewRungSection = (sec.title || "").toLowerCase().includes("new ladder position");
          return (
            <React.Fragment key={`${sec.title}-${i}`}>
              <motion.article
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: Math.min(i * 0.06, 0.3) }}
                data-testid={`progress-packet-section-${i}`}
              >
                <h2 className="font-serif-display text-2xl sm:text-3xl text-[#FFE7A0] mb-5 leading-tight">
                  {sec.title}
                </h2>
                <div className="text-[#F0E6D2]/90 text-base sm:text-lg leading-[1.7] font-body font-light whitespace-pre-wrap">
                  {sec.body}
                </div>
              </motion.article>
              {isNewRungSection && <LadderDiagram activeRung={packet.new_rung} />}
            </React.Fragment>
          );
        })}
      </section>
    </main>
  );
}
